import React, { useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import ChecklistRenderer from "./ChecklistRenderer";
import EvidenceUploader from "./EvidenceUploader";
import ProcessHistory from "./ProcessHistory";
import ProcessStatusBadge from "./ProcessStatusBadge";

const buildChecklistState = (items = []) =>
  items.reduce((acc, item) => {
    acc[item.codigo] = {
      cumplido: !!item.cumplido,
      observacion: item.observacion || "",
    };
    return acc;
  }, {});

export default function ProcessActionModal({
  show,
  onHide,
  detail,
  existingEvidences = [],
  submitting = false,
  onSubmit,
}) {
  const instance = detail?.instancia;
  const isReadOnly = !!instance?.esSoloLectura || instance?.puedeEjecutar === false;
  const [resultado, setResultado] = useState("");
  const [observacion, setObservacion] = useState("");
  const [duracionSegundos, setDuracionSegundos] = useState("");
  const [numeroContactado, setNumeroContactado] = useState("");
  const [esperaMinimaCumplida, setEsperaMinimaCumplida] = useState(false);
  const [permitirAntesDeFecha, setPermitirAntesDeFecha] = useState(false);
  const [files, setFiles] = useState([]);
  const [checklistValues, setChecklistValues] = useState({});

  React.useEffect(() => {
    if (!instance) return;
    setResultado(instance.resultadosPermitidos?.[0] || "");
    setObservacion("");
    setDuracionSegundos("");
    setNumeroContactado("");
    setEsperaMinimaCumplida(false);
    setPermitirAntesDeFecha(false);
    setFiles([]);
    setChecklistValues(buildChecklistState(instance.checklist || []));
  }, [instance?.id]);

  const checklistPayload = useMemo(
    () =>
      Object.entries(checklistValues).map(([codigo, item]) => ({
        codigo,
        cumplido: !!item.cumplido,
        observacion: item.observacion || "",
      })),
    [checklistValues]
  );

  const onConfirm = () => {
    if (isReadOnly) {
      return;
    }

    onSubmit?.({
      resultado,
      observacion,
      duracionSegundos: duracionSegundos ? Number(duracionSegundos) : null,
      numeroContactado,
      esperaMinimaCumplida,
      permitirAntesDeFecha,
      checklist: checklistPayload,
      evidencias: files,
      versionToken: instance?.versionToken,
      fechaHoraEvidencia: new Date().toISOString(),
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{instance?.procesoLabel || instance?.procesoCodigo || "CRM Postventa V2"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!instance ? (
          <div className="py-5 text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            <div className="row g-3">
              <div className="col-md-4">
                <strong>Comercio:</strong> {instance.comercio || "-"}
              </div>
              <div className="col-md-2">
                <strong>CUIT:</strong> {instance.cuit || "-"}
              </div>
              <div className="col-md-3">
                <strong>Comercio N:</strong> {instance.numeroComercio || "-"}
              </div>
              <div className="col-md-3">
                <strong>Terminal:</strong> {instance.numeroTerminal || "-"}
              </div>
              <div className="col-md-3">
                <strong>Estado:</strong> <ProcessStatusBadge>{instance.estadoLabel || instance.estadoActual}</ProcessStatusBadge>
              </div>
              <div className="col-md-3">
                <strong>Proceso origen:</strong> {instance.procesoOrigenLabel || instance.procesoOrigenCodigo || "-"}
              </div>
              <div className="col-md-3">
                <strong>Prioridad:</strong> {instance.prioridad || "-"}
              </div>
              <div className="col-md-3">
                <strong>Sector destino:</strong> {instance.sectorDestino || "-"}
              </div>
            </div>

            {instance.esSoloLectura ? (
              <Alert variant="info" className="mb-0">
                {instance.mensajeSoloLectura || "Gestion disponible en futuro modulo Compliance."}
              </Alert>
            ) : null}

            {!!instance.motivoBloqueoAccion && !instance.esSoloLectura ? (
              <Alert variant={instance.puedeEjecutar ? "info" : "warning"}>
                {instance.puedeEjecutar ? "Accion habilitada." : `Bloqueo actual: ${instance.motivoBloqueoAccion}`}
              </Alert>
            ) : null}

            <Alert variant="secondary" className="mb-0">
              <div><strong>Mensaje sugerido:</strong> {instance.mensajeSugerido || "-"}</div>
              <div><strong>Canales permitidos:</strong> {(instance.canalesPermitidos || []).join(", ") || "-"}</div>
              <div><strong>Campos obligatorios:</strong> {(instance.camposObligatorios || []).map((item) => item.etiqueta).join(", ") || "-"}</div>
            </Alert>

            <div className="row g-3">
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Resultado</Form.Label>
                  <Form.Select value={resultado} onChange={(event) => setResultado(event.target.value)} disabled={isReadOnly}>
                    <option value="">Seleccionar</option>
                    {(instance.resultadosPermitidos || []).map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Numero contactado</Form.Label>
                  <Form.Control value={numeroContactado} onChange={(event) => setNumeroContactado(event.target.value)} disabled={isReadOnly} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label>Duracion en segundos</Form.Label>
                  <Form.Control type="number" min="0" value={duracionSegundos} onChange={(event) => setDuracionSegundos(event.target.value)} disabled={isReadOnly} />
                </Form.Group>
              </div>
              <div className="col-12">
                <Form.Group>
                  <Form.Label>Observacion</Form.Label>
                  <Form.Control as="textarea" rows={3} value={observacion} onChange={(event) => setObservacion(event.target.value)} disabled={isReadOnly} />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Check
                  type="checkbox"
                  label="Confirmar espera minima cumplida"
                  checked={esperaMinimaCumplida}
                  onChange={(event) => setEsperaMinimaCumplida(event.target.checked)}
                  disabled={isReadOnly}
                />
              </div>
              <div className="col-md-6">
                <Form.Check
                  type="checkbox"
                  label="Permitir gestion antes de la fecha programada"
                  checked={permitirAntesDeFecha}
                  onChange={(event) => setPermitirAntesDeFecha(event.target.checked)}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            <div>
              <h5 className="mb-2">Checklist</h5>
              <ChecklistRenderer
                items={instance.checklist || []}
                editable={!isReadOnly}
                values={checklistValues}
                onChange={(codigo, nextValue) =>
                  setChecklistValues((prev) => ({
                    ...prev,
                    [codigo]: nextValue,
                  }))
                }
              />
            </div>

            <div>
              <h5 className="mb-2">Evidencias nuevas</h5>
              <EvidenceUploader files={files} onChange={setFiles} disabled={submitting || isReadOnly} />
            </div>

            <div>
              <h5 className="mb-2">Evidencias registradas</h5>
              {!existingEvidences.length ? (
                <div className="text-muted small">No hay evidencias persistidas todavia.</div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {existingEvidences.map((item) => (
                    <div key={item.id} className="border rounded p-2 bg-light d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-semibold">{item.nombreArchivo || "evidencia"}</div>
                        <div className="small text-muted">{item.tipo || item.mimeType || "-"}</div>
                      </div>
                      <a className="btn btn-outline-secondary btn-sm" href={`/api/crm-postventa-v2/evidencias/${item.id}/descargar`}>
                        Descargar
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h5 className="mb-2">Historial</h5>
              <ProcessHistory rows={detail?.historial || []} />
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>Cerrar</Button>
        {!isReadOnly ? (
          <Button variant="success" onClick={onConfirm} disabled={submitting || !resultado || !instance?.puedeEjecutar}>
            {submitting ? "Guardando..." : "Registrar accion"}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}
